import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import type { Address } from '@shared/models/address';
import { getAddressLine1 } from '@shared/validation/address';
import { BranchStore } from '@tvd/data-access/branch';
import { BottomBarService } from '../bottom-bar.service';
import { TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { BottomBarComponent } from '../bottom-bar';
import { Icon } from '@tvd/design-system/icon';

@Component({
  selector: 'bottom-bar-bookmark',
  imports: [TranslatePipe, Icon],
  host: {
    class: 'block w-full',
    '(click)': '$event.stopPropagation()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="bg-card">
      <div class="p-4">
        <h2 class="text-lg font-bold">
          {{ 'bottomBar.selectShop' | translate }}
        </h2>
        <p class="text-sm text-foreground/70">
          {{ 'bottomBar.selectShopDescription' | translate }}
        </p>
        <small class="text-xs text-foreground/60">
          {{ 'bottomBar.selectShopHint' | translate }}
        </small>
        <div class="flex flex-col gap-2 mt-4">
          @for (branch of branches(); track branch.id) {
            <button
              class="relative flex items-start justify-between p-4 rounded-lg outline outline-card-border"
              [class.bg-selected]="selectedBranch() === branch.id"
              [class.outline-select]="selectedBranch() === branch.id"
              (click)="selectBranch(branch.id)"
            >
              <div class="flex flex-col items-start">
                <h2 class="text-lg font-bold">{{ branch.name }}</h2>
                <p class="text-sm">
                  {{ formatLine(branch.address) }}, {{ branch.address.city }}
                  {{ branch.address.zip }}
                </p>
              </div>
              @if (selectedBranch() === branch.id) {
                <tvd-icon
                  icon="bookmark_check"
                  class="text-secondary !text-3xl"
                />
              }
            </button>
          }
        </div>
      </div>
    </div>
  `,
})
export class BottomBarBookmarkComponent {
  readonly #bottomBarService = inject(BottomBarService);
  readonly #router = inject(Router);
  readonly branchStore = inject(BranchStore);
  readonly branches = this.branchStore.branches;
  readonly selectedBranch = this.#bottomBarService.selectedBranchId;
  readonly #bottomBar = inject(BottomBarComponent);

  formatLine(address: Address): string {
    return getAddressLine1(address);
  }

  selectBranch(branchId: string) {
    this.#bottomBarService.setSelectedBranch(branchId);
    const org = this.branches().find((b) => b.id === branchId)?.organisation;
    if (!org) return;
    this.#router.navigate(['/dashboard', org, branchId]);
    this.#bottomBar.closeAction();
  }
}
